/**
 * ProgressBar — the app's single determinate progress track: Action Blue fill
 * on a neutral hairline track, honouring prefers-reduced-motion. Owns the
 * progressbar ARIA contract (role, min/max/now, polite live announcements) so
 * callers can't ship an unannounced bar. Used by Onboarding's interview
 * progress and AllocationBoard's assignment progress — extend here, don't
 * hand-roll a second track/fill pair.
 */

import { cn } from '@/lib/utils'

interface ProgressBarProps {
  /** Completed units, clamped into 0..max for both the fill and ARIA. */
  value: number
  /** Total units. A non-positive max renders an empty bar (never NaN). */
  max: number
  /** Accessible name announced with the progress. */
  label: string
  /** Track overrides (height, track color) — the fill is fixed by design. */
  className?: string
}

export function ProgressBar({ value, max, label, className }: ProgressBarProps) {
  const clamped = max > 0 ? Math.min(Math.max(value, 0), max) : 0
  const fraction = max > 0 ? clamped / max : 0
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max > 0 ? max : 0}
      aria-valuenow={clamped}
      aria-live="polite"
      className={cn('h-1 w-full overflow-hidden rounded-full bg-neutral-200', className)}
    >
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-300 motion-reduce:transition-none"
        style={{ width: `${fraction * 100}%` }}
      />
    </div>
  )
}
